import type { IntlShape } from 'react-intl';
import type { AddedAssetTheme, AddedUploadStartRefusal, AddedVideoRefusal } from 'fym-shared/src/music24/addedAssets';
import { MUSIC_VIDEO_ERROR_CODES } from 'fym-shared/src/music24/musicVideoApi';
import { isStoppedError, MasterPartError } from '../../../smartLinks/catalog/song/masterUploadParts';
import { addedAssetsMessages as messages } from './addedAssetsMessages';

/** Why an add failed, in the words of the dialog. */
export type AddedErrorKind =
  | AddedVideoRefusal
  | AddedUploadStartRefusal
  | 'notYouTube'
  | 'youtubePrivate'
  | 'youtubeNotFound'
  | 'uploadStopped'
  | 'failed';

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

const REFUSALS: ReadonlyArray<AddedVideoRefusal> = [
  'notAllowed',
  'type',
  'tooLarge',
  'unreadable',
  'tooShort',
  'tooLong',
  'shape',
  'tooSmall',
];

/**
 * The kind of a failed add (the `cause.code` of the `M24_ADDED_*` refusals, and `cause.reason` of
 * `M24_ADDED_REFUSED`). A part that S3 refused or that did not arrive is `uploadStopped`. Pure.
 */
export function addedErrorKind(error: unknown): AddedErrorKind {
  if (error instanceof MasterPartError) {
    return 'uploadStopped';
  }
  const cause = asRecord(asRecord(error)?.m24Limit);
  const code = cause?.code;
  switch (code) {
    case MUSIC_VIDEO_ERROR_CODES.addedNotAllowed:
      return 'notAllowed';
    case MUSIC_VIDEO_ERROR_CODES.addedTooLarge:
      return 'tooLarge';
    case MUSIC_VIDEO_ERROR_CODES.addedType:
      return 'type';
    case MUSIC_VIDEO_ERROR_CODES.addedYouTubeInvalid:
      return 'notYouTube';
    case MUSIC_VIDEO_ERROR_CODES.addedYouTubePrivate:
      return 'youtubePrivate';
    case MUSIC_VIDEO_ERROR_CODES.addedYouTubeNotFound:
      return 'youtubeNotFound';
    case MUSIC_VIDEO_ERROR_CODES.addedRefused: {
      const reason = REFUSALS.find((one) => one === cause?.reason);
      return reason ?? 'unreadable';
    }
    default:
      return 'failed';
  }
}

/**
 * Why "Remove" of an added version failed: `published` (409 `M24_ADDED_PUBLISHED`: a live YouTube or
 * Facebook publish of the video; the artist cancels or removes the publish first), else `failed`. Pure.
 */
export function removeAddedErrorKind(error: unknown): 'published' | 'failed' {
  const cause = asRecord(asRecord(error)?.m24Limit);
  return cause?.code === MUSIC_VIDEO_ERROR_CODES.addedPublished ? 'published' : 'failed';
}

/** True when the error is the stop of the user (no message to show). */
export function isAddedUploadStopped(error: unknown): boolean {
  return isStoppedError(error);
}

/** The sentence of a failed add. The Canvas has its own rules, so its sentences name them. Pure. */
export function addedErrorText(intl: IntlShape, kind: AddedErrorKind, themeId: AddedAssetTheme): string {
  const canvas = themeId === 'canvasLoop';
  switch (kind) {
    case 'notAllowed':
      return intl.formatMessage(messages.errorNotAllowed);
    case 'tooLarge':
      return intl.formatMessage(canvas ? messages.errorTooLargeCanvas : messages.errorTooLarge);
    case 'type':
      return intl.formatMessage(canvas ? messages.errorTypeCanvas : messages.errorType);
    case 'unreadable':
      return intl.formatMessage(messages.errorUnreadable);
    case 'tooShort':
      return intl.formatMessage(canvas ? messages.errorCanvasLength : messages.errorTooShort);
    case 'tooLong':
      return intl.formatMessage(canvas ? messages.errorCanvasLength : messages.errorTooLong);
    case 'shape':
      return intl.formatMessage(messages.errorShape);
    case 'tooSmall':
      return intl.formatMessage(messages.errorTooSmall);
    case 'notYouTube':
      return intl.formatMessage(messages.errorNotYouTube);
    case 'youtubePrivate':
      return intl.formatMessage(messages.errorPrivate);
    case 'youtubeNotFound':
      return intl.formatMessage(messages.errorNotFound);
    case 'uploadStopped':
      return intl.formatMessage(messages.errorUploadStopped);
    case 'failed':
      return intl.formatMessage(messages.errorFailed);
  }
}
